/**
 * The `/…/version-history` hub URL, and the one question a document page has
 * to answer before linking to it: does this document have a family at all?
 *
 * URL shape: the hub hangs off the document's own path
 * (`/documents/doe-o-4251e-…/version-history`), the same way core hangs
 * `/**\/edit` and `/**\/links-to-item` off theirs. Every member of a family
 * therefore has a working hub URL and they all render the same list — a reader
 * who lands on the 1994 revision reaches the hub without having to find the
 * current one first. Only ONE of them is canonical (the backend names it in
 * `canonical_url`) and only that one goes in the sitemap, so N members do not
 * become N indexable copies of one page.
 */
export const HISTORY_SEGMENT = 'version-history';

/** `/documents/x/version-history` -> `/documents/x` */
export const documentPathOf = (pathname) => {
  const clean = String(pathname || '').replace(/\/+$/, '');
  const suffix = `/${HISTORY_SEGMENT}`;
  return clean.endsWith(suffix) ? clean.slice(0, -suffix.length) || '/' : clean;
};

/** `/documents/x` -> `/documents/x/version-history` */
export const timelineUrlOf = (path) =>
  `${String(path || '').replace(/\/+$/, '')}/${HISTORY_SEGMENT}`;

/**
 * How many documents are in this document's version family, INCLUDING itself.
 *
 * Read off the `document_relations` expander's graph, which the document page
 * already carries (it is in `apiExpanders`, so it is in the server response) —
 * the graph is the same recursive supersedes+cancels walk the timeline
 * endpoint runs, with the same private-node exclusion. Asking the backend
 * again just to decide whether to render one link would cost a request on
 * every document page to answer a question already in hand.
 *
 * Returns 1 for a document with no supersession relations at all, which is the
 * signal not to link a hub page whose only entry would be the page you are on.
 */
export const familySize = (content) => {
  const nodes = content?.['@components']?.document_relations?.graph?.nodes;
  return Array.isArray(nodes) ? nodes.length : 1;
};

export const hasFamily = (content) => familySize(content) > 1;
